<template>
  <form :id="formId" :ref="formId" method="POST" class="dropzone upload-box">
    <input type="hidden" name="_token" :value="$csrf_token">
    <input type="hidden" name="_method" :value="formMethod">
  </form>
</template>

<script>
  import { v4 as uuidv4 } from 'uuid';
  import Dropzone from "dropzone";

  export default {
    emits: ['update:files', 'addedfile', 'addedfiles', 'removedfile', 'sending', 'success', 'error', 'queuecomplete', 'complete', 'initialized'],

    props: {

      files: {
        type: Array,
        default: () => {return []}
      },

      // Form ID.
      id: {
        type: String,
        default: '',
      },

      // Url for attachment upload request.
      url: {
        required: true,
        type: String,
      },

      // Parameter name for uploading attachment.
      paramName: {
        type: String,
        default: 'file'
      },

      // Form method for uploading attachment.
      formMethod: {
        type: String,
        default: 'POST'
      },

      // The default implementation of `accept` checks the file's mime type or
      // extension against this list. This is a comma separated list of mime
      // types or file extensions.
      acceptedFiles: {
        type: String,
        default: null
      },

      // The maximum filesize (in MiB) that is allowed to be uploaded.
      maxFilesize: {
        type: Number,
        default: 10
      },

      // If not `null` defines how many files this Dropzone handles.
      maxFiles: {
        type: Number,
        default: null
      },

      // How many file uploads to process in parallel.
      parallelUploads: {
        type: Number,
        default: 2
      },

      // The timeout for the XHR requests in milliseconds.
      timeout: {
        type: Number,
        default: 0
      },

      // The maximum filesize (in MiB) that is allowed to be uploaded.
      uploadMultiple: {
        type: Boolean,
        default: false
      },

      // If false, files will be added to the queue but the queue will not be processed automatically.
      autoUpload: {
        type: Boolean,
        default: false
      },

      // If `true`, this will add a link to every file preview to remove or cancel (if already uploading) the file.
      addRemoveLinks: {
        type: Boolean,
        default: true
      },

      previewTemplate: {
        type: String,
        default: ''
      },

      placeholder: {
        type: String,
        default: '<h3>Drag and drop files here, or Click to select files</h3>'
      },

      // If `null`, the ratio of the image will be used to calculate it.
      thumbnailWidth: {
        type: Number,
        default: null
      },

      // The same as `thumbnailWidth`. If both are null, images will not be resized.
      thumbnailHeight: {
        type: Number,
        default: null
      }
    },

    data() {
      return {
        dropzone: null,
        formId: this.id || 'dropzone_form_' + uuidv4(),

        /**
         * All available options and events are here:
         * https://github.com/dropzone/dropzone/blob/main/src/options.js
         * or in dropzone/src/options (imported here as "defaultOptions").
         */
        options: {
          // Configuration based on component props.
          url: this.url,
          paramName: this.paramName,
          maxFilesize: this.maxFilesize,
          maxFiles: this.maxFiles,
          acceptedFiles: this.acceptedFiles,
          uploadMultiple: this.uploadMultiple,
          autoProcessQueue: this.autoUpload,
          dictDefaultMessage: this.placeholder,
          addRemoveLinks: this.addRemoveLinks,
          parallelUploads: this.parallelUploads,
          timeout: this.timeout,
          thumbnailWidth: this.thumbnailWidth,
          thumbnailHeight: this.thumbnailHeight,
        },

        events: {
          'addedfile': (file) => this.$emit('addedfile', file),
          'addedfiles': () => this.$emit('addedfiles'),
          'removedfile': (file) => {
            this.$emit('removedfile', file);
            this.$emit('update:files', this.getAcceptedFiles());
          },
          'sending': (file, xhr, formData) => this.$emit('sending', file, xhr, formData),
          'success': (file, response, e) => {
            this.$emit('success', file, response, e);
            this.$emit('update:files', this.getAcceptedFiles());
            this.dropzone.processQueue();
          },
          'error': (file, message, xhr) => this.$emit('error', file, message, xhr),
          'queuecomplete': () => this.$emit('queuecomplete'),
          'complete': (file) => this.$emit('complete', file),
        },
      };
    },

    mounted: function () {
      this.$nextTick(function () {
        if (this.previewTemplate !== '') {
          // @todo Use slot to get previewTemplate.
          this.options.previewTemplate = this.previewTemplate;
        }

        this.dropzone = new Dropzone("form#" + this.formId, this.options);

        Object.keys(this.events).forEach(eventName => {
          this.dropzone.on(eventName, this.events[eventName]);
        });

        this.files.forEach(item => {
          var file = { name: item.name, size: item.size, type: item.type, accepted: true, upload: {uuid: item.uuid} };
          this.manuallyAddFile(file, '');
          if (item.dataURL) {
            this.dropzone.emit("thumbnail", file, item.dataURL);
          }
        });

        this.$emit('initialized');
      })
    },

    methods: {
      /**
       * Add parameters to transfer to the server.
       */
      addParams(params) {
        this.dropzone.options.params = params;
      },

      /**
       * Wrapper for DropZone processQueue.
       */
      process() {
        this.dropzone.processQueue();
      },

      /**
       * Wrapper for DropZone removeFile.
       */
      removeFile(file) {
        this.dropzone.removeFile(file);
      },

      /**
       * Wrapper for DropZone getAcceptedFiles.
       */
      getAcceptedFiles() {
        return this.dropzone.getAcceptedFiles();
      },

      /**
       * Wrapper for DropZone getRejectedFiles.
       */
      getRejectedFiles() {
        return this.dropzone.getRejectedFiles();
      },

      /**
       * Wrapper for DropZone getQueuedFiles.
       */
      getQueuedFiles() {
        return this.dropzone.getQueuedFiles();
      },

      /**
       * Wrapper for DropZone getUploadingFiles.
       */
      getUploadingFiles() {
        return this.dropzone.getUploadingFiles();
      },

      /**
       * Add file to Dropzone (copied from vue-dropzone package).
       *
       * @param file
       * @param fileUrl
       */
      manuallyAddFile: function(file, fileUrl) {
        file.manuallyAdded = true;
        this.dropzone.emit("addedfile", file);
        let containsImageFileType = false;
        if (
          fileUrl.indexOf(".svg") > -1 ||
          fileUrl.indexOf(".png") > -1 ||
          fileUrl.indexOf(".jpg") > -1 ||
          fileUrl.indexOf(".jpeg") > -1 ||
          fileUrl.indexOf(".gif") > -1 ||
          fileUrl.indexOf(".webp") > -1
        )
          containsImageFileType = true;
        if (
          this.dropzone.options.createImageThumbnails &&
          containsImageFileType &&
          file.size <= this.dropzone.options.maxThumbnailFilesize * 1024 * 1024
        ) {
          fileUrl && this.dropzone.emit("thumbnail", file, fileUrl);

          var thumbnails = file.previewElement.querySelectorAll(
            "[data-dz-thumbnail]"
          );
          for (var i = 0; i < thumbnails.length; i++) {
            thumbnails[i].style.width =
              this.dropzoneSettings.thumbnailWidth + "px";
            thumbnails[i].style.height =
              this.dropzoneSettings.thumbnailHeight + "px";
            thumbnails[i].style["object-fit"] = "contain";
          }
        }
        this.dropzone.emit("complete", file);
        if (this.dropzone.options.maxFiles) this.dropzone.options.maxFiles--;
        this.dropzone.files.push(file);
        this.$emit('addedfile', file)
      },

    }
  }

</script>
